import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { AuthenticationProvider, AuthenticationService } from '@aodocs/authentication';
import { TranslocoUtils } from '@aodocs/shared-translation';
import { User } from '@firebase/auth';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { UserCredential } from 'firebase/auth';
import * as firebaseui from 'firebaseui';

import { firebaseConfigConst } from '../../../const/firebase-config.const';
import { AO_PRIVACY_POLICY_URL, AO_TERM_OF_SERVICE_URL } from '../../../const/firebase-url.const';

@Component({
  selector: 'aodocs-password-form-sign-in',
  standalone: true,
  imports: [CommonModule, TranslocoModule, MatIcon, MatButton],
  providers: [
    provideTranslocoScope({
      scope: 'authentication',
      loader: TranslocoUtils.loader((lang) => import(`../../../../assets/i18n/lib/authentication/authentication-${lang}.json`)),
    }),
  ],
  templateUrl: './password-form-sign-in.component.html',
  styleUrls: ['./password-form-sign-in.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class PasswordFormSignInComponent implements AfterViewInit, OnDestroy {
  @ViewChild('firebseuiAuthContainer') firebaseAuthContainer?: ElementRef<HTMLDivElement>;

  @Output() clickEvent = new EventEmitter<void>();
  @Output() emailSent = new EventEmitter<string>();
  private firebaseUi?: firebaseui.auth.AuthUI;

  constructor(
    private readonly authenticationService: AuthenticationService,
    private readonly renderer: Renderer2,
    protected readonly translocoService: TranslocoService
  ) {}

  ngAfterViewInit(): void {
    this.firebaseUi = this.authenticationService.generateFirebaseUI();
    const uiConfig: firebaseui.auth.Config = {
      signInSuccessUrl: '/#',
      signInOptions: [
        {
          provider: AuthenticationProvider.PASSWORD,
          requireDisplayName: false,
        },
      ],
      tosUrl: AO_TERM_OF_SERVICE_URL,
      privacyPolicyUrl: () => {
        const privacyPolicyA = this.renderer.createElement('a');
        privacyPolicyA.rel = 'noopener';
        privacyPolicyA.target = '_blank';
        privacyPolicyA.href = AO_PRIVACY_POLICY_URL;
        privacyPolicyA.click();
        privacyPolicyA.remove();
      },
      callbacks: {
        signInSuccessWithAuthResult: (authResult: UserCredential) => {
          if (!authResult.user.emailVerified) {
            this.handleUnverifiedUser(authResult.user);
          }
          return false;
        },
        signInFailure: (error) => {
          console.error(error);
        },
      },
    };

    this.firebaseUi.start('#firebseui-auth-container', uiConfig);

    this.setFirebaseButtonTrigger();
  }

  ngOnDestroy(): void {
    this.firebaseUi?.delete().then();
  }

  private handleUnverifiedUser(user: User): void {
    this.authenticationService.sendConfirmationEmail(user).then();
    this.emailSent.emit(user.email ?? '');
    this.clickEvent.emit();
  }

  private setFirebaseButtonTrigger(): void {
    this.setFirebaseUiDescription();
    this.setCancelTrigger();
    this.setSubmitTrigger();
  }
  private setFirebaseUiDescription(): void {
    if (!this.getUIElement<HTMLParagraphElement>('#singin-description')) {
      const card = this.getUIElement<HTMLDivElement>('.firebaseui-card-content');
      const p = this.renderer.createElement('p');

      p.id = firebaseConfigConst.id;
      p.innerText = this.translocoService.translate('authentication.CREATION_ACCOUNT_DISCLAIMER');
      p.style.fontWeight = firebaseConfigConst.fontWeight;
      p.style.fontFamily = firebaseConfigConst.fontFamily;
      p.style.fontSize = firebaseConfigConst.fontSize;
      p.style.lineHeight = firebaseConfigConst.lineHeight;
      p.style.color = firebaseConfigConst.color;
      p.style.margin = firebaseConfigConst.margin;

      card?.insertAdjacentElement('afterbegin', p);
    }
  }

  private setCancelTrigger(): void {
    this.getUIElement<HTMLButtonElement>('button.firebaseui-id-secondary-link')?.addEventListener('click', () =>
      this.setFirebaseButtonTrigger()
    );
  }

  private setSubmitTrigger(): void {
    this.getUIElement<HTMLButtonElement>('button.firebaseui-id-submit')?.addEventListener('click', () => {
      setTimeout(() => {
        this.setFirebaseUiDescription();
        this.setCancelTrigger();
      }, 500);
    });
  }

  private getUIElement<E extends Element = Element>(selector: string): E | undefined {
    return this.firebaseAuthContainer?.nativeElement.querySelector<E>(selector) ?? undefined;
  }
}
